import type {
	DokumentyDokumentDto,
	DokumentyDokumentSeznamDto,
} from '@gov-nx/api';
import { useLocale } from '@gov-nx/core/service';
import type { FilterParam, Optional } from '@gov-nx/core/types';
import { pipe } from '@gov-nx/core/types';
import {
	addDay,
	resetHours,
	toDate,
	toDateReadable,
	useBoolean,
	useFilter,
} from '@gov-nx/utils/common';
import type { PageCode } from '../../../definitions/codes';
import type {
	MyFilesContextType,
	MyFilesFilterType,
	MyFilesFormData,
	MyFilesOrder,
	RecycleBinOrder,
} from '../context.types';

const filterDateRange =
	(from?: Date, to?: Date) => (document: DokumentyDokumentDto) => {
		const date = document.datumVytvoreni
			? toDate(document.datumVytvoreni)
			: undefined;
		if (!date) return true;

		if (from && date < resetHours(from)) {
			return false;
		}

		if (to && date > pipe(to, addDay(), resetHours)) {
			return false;
		}

		return true;
	};

interface MyFilesFiltersProps {
	code: PageCode;
	formMethods: MyFilesContextType['formDefinition']['formMethods'];
	initialState: MyFilesFilterType;
}

export const useMyFilesFilters = ({
	formMethods,
	code,
	initialState,
}: MyFilesFiltersProps) => {
	const { t: tsn } = useLocale(code);

	const filterWindowOpened = useBoolean(false);

	const { filter, setPartialFilter } = useFilter<MyFilesFilterType>({
		key: code,
		initialState,
		rehydrate: (data) => ({
			...data,
			datumVytvoreniOd:
				typeof data.datumVytvoreniOd === 'string'
					? toDate(data.datumVytvoreniOd)
					: data.datumVytvoreniOd,
			datumVytvoreniDo:
				typeof data.datumVytvoreniDo === 'string'
					? toDate(data.datumVytvoreniDo)
					: data.datumVytvoreniDo,
		}),
	});

	const goToPage = (page: number) => {
		setPartialFilter({ start: (page - 1) * filter.pageSize });
	};

	const selectBadge = (selectedBadgeIndex: number) => {
		setPartialFilter({ selectedBadgeIndex, start: 0 });
	};

	const submitFilters = (values: MyFilesFormData) => {
		setPartialFilter({ ...values, start: 0 });
		filterWindowOpened.setFalse();
	};

	const filterDocuments = (
		documents?: DokumentyDokumentSeznamDto
	): Optional<DokumentyDokumentSeznamDto> => {
		if (!documents) return undefined;
		return {
			seznam: documents.seznam?.filter(
				filterDateRange(filter.datumVytvoreniOd, filter.datumVytvoreniDo)
			),
			pocet: documents.pocet,
		};
	};

	const sortFilesBy = (sortParam: MyFilesOrder | RecycleBinOrder) =>
		setPartialFilter({ razeni: sortParam, start: 0 });

	const resetFilter = () => {
		formMethods.setValue('text', undefined);
		setPartialFilter({
			datumVytvoreniDo: undefined,
			datumVytvoreniOd: undefined,
			text: undefined,
		});
		filterWindowOpened.setFalse();
	};

	const filterParams: FilterParam[] = [
		{
			label: tsn('formular.pole.obdobi-od'),
			value: filter.datumVytvoreniOd
				? toDateReadable(filter.datumVytvoreniOd)
				: undefined,
			isActive: !!filter.datumVytvoreniOd,
			onRemove: () => {
				setPartialFilter({ datumVytvoreniOd: undefined });
			},
		},
		{
			label: tsn('formular.pole.obdobi-do'),
			value: filter.datumVytvoreniDo
				? toDateReadable(filter.datumVytvoreniDo)
				: undefined,
			isActive: !!filter.datumVytvoreniDo,
			onRemove: () => {
				setPartialFilter({ datumVytvoreniDo: undefined });
			},
		},
	];

	const isNotFirstPage = (filter: MyFilesFilterType) => filter.start !== 0;

	const isEmpty = <T>(list?: T[]) => list?.length === 0;

	return {
		setPartialFilter,
		submitFilters,
		goToPage,
		selectBadge,
		filter,
		filterWindowOpened,
		filterDocuments,
		resetFilter,
		filterParams,
		sortFilesBy,
		isNotFirstPage,
		isEmpty,
		pageNumber: filter.start / filter.pageSize + 1,
	};
};
